import React from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, hexToRgba } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import SectionIcon from './SectionIcon';

export default function ActivityFeedSection({ block = {}, primaryColor, secondaryColor, enableAnimations }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const items = content.items || [];
    const variant = config.variant || 'timeline';

    if (items.length === 0) return null;

    const isStory = variant === 'story';
    const textColor = isStory ? '#fff' : s.text;
    const textSub = isStory ? 'rgba(255,255,255,0.6)' : s.textSub;
    const bgStyle = isStory ? { backgroundColor: '#111827' } : s.bgStyle;

    return (
        <section ref={ref} className={`relative ${isStory ? '' : s.padClass}`} style={bgStyle}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div className={`relative ${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${isStory ? 'py-20' : ''}`}>
                <SectionHeader
                    title={content.title}
                    subtitle={content.subtitle}
                    tag={content.tag}
                    accent={s.accent}
                    secondary={s.secondary}
                    textColor={textColor}
                    textSub={textSub}
                    anim={anim}
                />

                {variant === 'timeline' && (
                    <div className="relative mt-12">
                        <div className="absolute left-4 top-0 bottom-0 w-0.5" style={{ backgroundColor: hexToRgba(s.accent, 0.2) }} />
                        <div className="space-y-8">
                            {items.map((item, i) => (
                                <div
                                    key={i}
                                    className={`relative pl-12 ${anim}`}
                                    style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}
                                >
                                    <div className="absolute left-2.5 top-1 w-3 h-3 rounded-full border-2" style={{ borderColor: s.accent, backgroundColor: s.bg || '#fff' }} />
                                    <div className="flex items-center gap-3 mb-1">
                                        {item.time && (
                                            <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: hexToRgba(s.accent, 0.1), color: s.accent }}>
                                                {item.time}
                                            </span>
                                        )}
                                        {item.badge && (
                                            <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: hexToRgba(s.secondary, 0.1), color: s.secondary }}>
                                                {item.badge}
                                            </span>
                                        )}
                                    </div>
                                    <h4 className="font-semibold text-base" style={{ color: textColor }}>{item.title}</h4>
                                    {item.description && <p className="text-sm mt-1" style={{ color: textSub }}>{item.description}</p>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {variant === 'feed' && (
                    <div className="mt-12 space-y-4">
                        {items.map((item, i) => (
                            <div
                                key={i}
                                className={`flex items-start gap-4 p-4 rounded-xl ${anim}`}
                                style={{ backgroundColor: hexToRgba(s.accent, 0.04), transitionDelay: visible ? `${i * 100}ms` : '0ms' }}
                            >
                                <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: hexToRgba(s.accent, 0.1) }}>
                                    {item.icon ? <SectionIcon name={item.icon} size={20} color={s.accent} /> : <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.accent }} />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2">
                                        <h4 className="font-semibold text-sm truncate" style={{ color: textColor }}>{item.title}</h4>
                                        {item.time && <span className="text-xs flex-shrink-0" style={{ color: textSub }}>{item.time}</span>}
                                    </div>
                                    {item.description && <p className="text-sm mt-0.5" style={{ color: textSub }}>{item.description}</p>}
                                    {item.badge && (
                                        <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full mt-2" style={{ backgroundColor: hexToRgba(s.secondary, 0.1), color: s.secondary }}>
                                            {item.badge}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {variant === 'story' && (
                    <div className="relative mt-12">
                        <div className="absolute left-4 top-0 bottom-0 w-0.5" style={{ backgroundColor: hexToRgba(s.accent, 0.3) }} />
                        <div className="space-y-10">
                            {items.map((item, i) => (
                                <div
                                    key={i}
                                    className={`relative pl-12 ${anim}`}
                                    style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}
                                >
                                    <div className="absolute left-2 top-1 w-4 h-4 rounded-full" style={{ backgroundColor: s.accent, boxShadow: `0 0 12px ${hexToRgba(s.accent, 0.6)}` }} />
                                    <div className="flex items-center gap-3 mb-2">
                                        {item.time && <span className="text-xs font-mono" style={{ color: hexToRgba(s.accent, 0.8) }}>{item.time}</span>}
                                        {item.badge && (
                                            <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: hexToRgba(s.accent, 0.15), color: s.accent }}>
                                                {item.badge}
                                            </span>
                                        )}
                                    </div>
                                    <h4 className="font-bold text-lg" style={{ color: '#fff' }}>{item.title}</h4>
                                    {item.description && <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.55)' }}>{item.description}</p>}
                                    {item.icon && (
                                        <div className="mt-2">
                                            <SectionIcon name={item.icon} size={20} color={hexToRgba(s.accent, 0.7)} />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
